import { ImgHTMLAttributes } from "react";
import { useFetchDocImage } from "utils/query";

/**
 * Resolves an image src to its path under the server's docs/ folder, the way a markdown preview
 * would: relative to the doc, or to docs/ itself with a leading slash. External URLs return null.
 */
export const resolveDocImagePath = (docSlug: string, src: string) => {
  if (/^([a-z][a-z0-9+.-]*:|\/\/)/i.test(src)) {
    return null;
  }
  return new URL(src, `https://docs.invalid/${docSlug}`).pathname.slice(1);
};

type MarkdownImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  docSlug: string;
  // react-markdown's hast node; dropped so it doesn't land on the <img> as an attribute.
  node?: unknown;
};

export const MarkdownImage = ({
  docSlug,
  src = "",
  alt = "",
  node: _node,
  ...rest
}: MarkdownImageProps) => {
  const path = resolveDocImagePath(docSlug, src);
  // An <img> cannot send the auth header, so doc images come through the API as data URLs.
  const { data } = useFetchDocImage(path ?? "");

  if (path === null) {
    return <img src={src} alt={alt} {...rest} />;
  }
  if (!data?.dataUrl) {
    // Loading or failed: keep the alt text so the doc still reads.
    return alt ? <em>{alt}</em> : null;
  }
  return <img src={data.dataUrl} alt={alt} {...rest} />;
};
